import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const CoAuthorManager = ({ coAuthors, onCoAuthorsChange }) => {
  const { t } = useTranslation('submissions');
  const k = (key) => t(`authorPortal.coAuthors.${key}`);

  const [newAuthor, setNewAuthor] = useState({ name: '', email: '', affiliation: '' });
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re?.test(email);
  };

  const handleAddAuthor = () => {
    const newErrors = {};

    if (!newAuthor?.name?.trim()) {
      newErrors.name = k('validation.nameRequired');
    }

    if (!newAuthor?.email?.trim()) {
      newErrors.email = k('validation.emailRequired');
    } else if (!validateEmail(newAuthor?.email)) {
      newErrors.email = k('validation.emailInvalid');
    }

    if (!newAuthor?.affiliation?.trim()) {
      newErrors.affiliation = k('validation.affiliationRequired');
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    onCoAuthorsChange([...coAuthors, { ...newAuthor, id: Date.now() }]);
    setNewAuthor({ name: '', email: '', affiliation: '' });
    setErrors({});
  };

  const handleRemoveAuthor = (id) => {
    onCoAuthorsChange(coAuthors?.filter(author => author?.id !== id));
  };

  const handleInputChange = (field, value) => {
    setNewAuthor(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-elevation-2">
      <div className="flex items-start gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Users" size={20} className="text-secondary" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-1">{k('title')}</h2>
          <p className="text-sm md:text-base text-muted-foreground">{k('subtitle')}</p>
        </div>
      </div>
      {coAuthors?.length > 0 && (
        <div className="space-y-3 mb-6">
          {coAuthors?.map((author, index) => (
            <div key={author?.id} className="flex items-start gap-3 p-3 md:p-4 bg-muted rounded-lg">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm md:text-base font-medium text-primary">{index + 2}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm md:text-base font-medium text-foreground">{author?.name}</p>
                <p className="text-xs md:text-sm text-muted-foreground">{author?.email}</p>
                <p className="text-xs md:text-sm text-muted-foreground">{author?.affiliation}</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveAuthor(author?.id)}
                iconName="Trash2"
              />
            </div>
          ))}
        </div>
      )}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={k('name.label')}
            type="text"
            placeholder={k('name.placeholder')}
            value={newAuthor?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
          />
          <Input
            label={k('email.label')}
            type="email"
            placeholder={k('email.placeholder')}
            value={newAuthor?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
          />
        </div>

        <Input
          label={k('affiliation.label')}
          type="text"
          placeholder={k('affiliation.placeholder')}
          value={newAuthor?.affiliation}
          onChange={(e) => handleInputChange('affiliation', e?.target?.value)}
          error={errors?.affiliation}
        />

        <Button
          type="button"
          variant="outline"
          size="default"
          onClick={handleAddAuthor}
          iconName="UserPlus"
          iconPosition="left"
          fullWidth
        >
          {k('add')}
        </Button>
      </div>
      <div className="mt-4 md:mt-6 p-3 md:p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={16} className="text-primary flex-shrink-0 mt-0.5" />
          <p className="text-xs md:text-sm text-muted-foreground">
            {k('info')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CoAuthorManager;
